import React, { useEffect, useState } from "react";
import { X, Download, Loader2 } from "lucide-react";
import api, { API_ROOT, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Preview a stored exam file. Fetches with credentials as a blob so cookie
 * auth flows correctly, then renders inside an <iframe> (PDF) or <img> (image).
 */
export default function ExamPreviewModal({ exam, onClose }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let revoked = null;
        (async () => {
            try {
                const resp = await api.get(`/exams/${exam.id}/file`, { responseType: "blob" });
                const url = URL.createObjectURL(resp.data);
                revoked = url;
                setBlobUrl(url);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
        return () => { if (revoked) URL.revokeObjectURL(revoked); };
    }, [exam.id]);

    const isImage = (exam.content_type || "").startsWith("image/");
    const isPdf = (exam.content_type || "").startsWith("application/pdf");

    const download = () => {
        if (!blobUrl) return;
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = exam.original_filename || `exame-${exam.id}`;
        a.click();
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            data-testid="exam-preview-modal"
        >
            <div className="bg-[var(--bg-primary)] rounded-lg shadow-xl w-full max-w-5xl h-[92vh] flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-[var(--border-organic)]">
                    <div>
                        <h2 className="font-display text-lg text-brand line-clamp-1">
                            {exam.exam_type_label || exam.exam_type}
                        </h2>
                        <p className="text-xs text-muted-clinical mt-0.5">
                            {exam.exam_date} · {exam.original_filename}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={download} className="btn-ghost text-sm" data-testid="exam-preview-download">
                            <Download size={14} /> Baixar
                        </button>
                        <button onClick={onClose} className="btn-ghost" data-testid="exam-preview-close">
                            <X size={18} />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto bg-[#1a1a1a] flex items-center justify-center">
                    {loading ? (
                        <Loader2 className="animate-spin text-white/60" size={32} />
                    ) : !blobUrl ? (
                        <p className="text-white/60 text-sm">Falha ao carregar preview.</p>
                    ) : isImage ? (
                        <img src={blobUrl} alt={exam.exam_type_label} className="max-w-full max-h-full" />
                    ) : isPdf ? (
                        <iframe title="preview" src={blobUrl} className="w-full h-full bg-white" />
                    ) : (
                        <div className="text-white/70 text-sm p-6 text-center">
                            <p>Não é possível pré-visualizar este tipo de arquivo.</p>
                            <button onClick={download} className="btn-primary mt-4"><Download size={14} /> Baixar arquivo</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
